import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import { resolve, type Source, SourceSchema } from '@/agent/refs';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldList,
  translateAttributes,
} from './shared/layers';
import { linearToEsri, type LinearUnit } from './shared/units';
import type { Tool } from './types';

type Direction = 'nearest' | 'farthest';

type Input = {
  origin: Source;
  target_layer_id: string;
  where?: string;
  limit?: number;
  max_distance_meters?: number;
  units?: LinearUnit;
  out_fields?: string[];
  direction?: Direction;
};

export const queryNearest: Tool<Input> = {
  name: 'query_nearest',
  description:
    'Rank features in target_layer_id by edge-to-edge distance from origin (any Source). Default direction is "nearest"; pass direction:"farthest" to find the most distant features instead. Use max_distance_meters to bound the search radius.',
  input_schema: {
    type: 'object',
    properties: {
      origin: SourceSchema,
      target_layer_id: { type: 'string' },
      where: { type: 'string' },
      limit: { type: 'integer', minimum: 1, maximum: 100 },
      max_distance_meters: { type: 'number', exclusiveMinimum: 0 },
      units: { enum: ['meters', 'kilometers', 'feet', 'miles'] },
      out_fields: { type: 'array', items: { type: 'string' } },
      direction: { enum: ['nearest', 'farthest'] },
    },
    required: ['origin', 'target_layer_id'],
    additionalProperties: false,
  },
  execute: async (input) => {
    const originRef = await resolve(input.origin);
    if (!originRef.geometry) throw new Error('query_nearest: origin has no geometry');

    const layer = await ensureFeatureLayer(input.target_layer_id);
    const probe = requireLayerRecord(input.target_layer_id).probe;
    const oidField = layer.objectIdField || 'objectid';

    const direction: Direction = input.direction ?? 'nearest';
    const isFarthest = direction === 'farthest';
    const baseWhere = input.where && input.where.trim() ? input.where : '1=1';
    const candidateMultiplier = isFarthest ? 50 : 10;
    const candidateCap = isFarthest ? 1000 : 200;
    const candidateLimit = Math.min(
      Math.max(input.limit ?? 5, 1) * candidateMultiplier,
      candidateCap,
    );

    const queryProps: __esri.QueryProperties = {
      where: baseWhere,
      outFields: resolveFieldList(input.out_fields, probe, layer) ?? ['*'],
      returnGeometry: true,
      num: candidateLimit,
    };
    if (input.max_distance_meters) {
      const buffered = geometryEngine.geodesicBuffer(
        originRef.geometry as Parameters<typeof geometryEngine.geodesicBuffer>[0],
        input.max_distance_meters,
        'meters',
      );
      const polygon = Array.isArray(buffered) ? buffered[0] : buffered;
      if (polygon) {
        queryProps.geometry = polygon;
        queryProps.spatialRelationship = 'intersects';
      }
    } else if (!isFarthest) {
      queryProps.geometry = originRef.extent ?? originRef.geometry;
      queryProps.spatialRelationship = 'intersects';
      queryProps.distance = undefined;
    }

    const rs = await layer.queryFeatures(queryProps);
    const unit = linearToEsri(input.units);

    const ranked = rs.features
      .filter((f) => !!f.geometry)
      .map((f) => {
        const dist = geometryEngine.distance(
          originRef.geometry as Parameters<typeof geometryEngine.distance>[0],
          f.geometry as Parameters<typeof geometryEngine.distance>[1],
          unit,
        );
        return { feature: f, distance: dist };
      })
      .filter((row) => !Number.isNaN(row.distance))
      .sort((a, b) => (isFarthest ? b.distance - a.distance : a.distance - b.distance))
      .slice(0, input.limit ?? 5);

    return {
      origin_kind: originRef.source_kind,
      direction,
      units: input.units ?? 'meters',
      candidates_scanned: rs.features.length,
      results: ranked.map((row) => ({
        layer_id: input.target_layer_id,
        objectid: row.feature.attributes?.[oidField],
        distance: row.distance,
        attributes: translateAttributes(row.feature.attributes ?? {}, probe),
      })),
    };
  },
};
